import { createContext, useContext, useEffect, useState } from "react";
import type { AbstractAgent } from "@ag-ui/client";

export interface SubagentEvent {
  subagent: string;
  toolCallId: string;
  kind: "tool_call" | "tool_result";
  toolName: string;
  summary?: string;
}

// Subscribes to AG-UI CustomEvent(name="subagent") and accumulates events
// keyed by the orchestrator-level tool_call_id. The backend emits these
// while a subagent (e.g. python_analyzer) runs inside a parent tool call,
// so the UI can show per-step progress (check/spinner/X) inside that
// parent's Progress tab. Events are streaming-only — cleared on each run
// start and not persisted past run completion.
export function useSubagentEvents(agent: AbstractAgent): ReadonlyMap<string, SubagentEvent[]> {
  const [events, setEvents] = useState<Map<string, SubagentEvent[]>>(() => new Map());

  useEffect(() => {
    const sub = agent.subscribe({
      onRunStartedEvent: () => {
        setEvents(new Map());
      },
      onCustomEvent: ({ event }: { event: unknown }) => {
        const e = event as { name?: string; value?: Record<string, unknown> };
        if (e.name !== "subagent") return;
        const v = e.value;
        if (!v?.tool_call_id || !v.kind || !v.tool_name) return;
        const kind = v.kind as string;
        if (kind !== "tool_call" && kind !== "tool_result") return;

        const evt: SubagentEvent = {
          subagent: (v.subagent as string) ?? "unknown",
          toolCallId: v.tool_call_id as string,
          kind: kind as SubagentEvent["kind"],
          toolName: v.tool_name as string,
          summary: v.summary as string | undefined,
        };

        setEvents((prev) => {
          const next = new Map(prev);
          next.set(evt.toolCallId, [...(next.get(evt.toolCallId) ?? []), evt]);
          return next;
        });
      },
    });
    return () => sub.unsubscribe();
  }, [agent]);

  return events;
}

const EMPTY_EVENTS: ReadonlyMap<string, SubagentEvent[]> = new Map();

const SubagentEventsContext = createContext<ReadonlyMap<string, SubagentEvent[]>>(EMPTY_EVENTS);

export const SubagentEventsProvider = SubagentEventsContext.Provider;

export function useSubagentEventsFor(toolCallId: string): SubagentEvent[] {
  const map = useContext(SubagentEventsContext);
  return map.get(toolCallId) ?? [];
}
